<template>
  <div class="MediaBankFilterGroup desk:border-b border-light">
    <div
      class="flex justify-between items-center cursor-pointer type-headline-sm leading-single mobOnly:mx-16 mobOnly:border-b border-light"
      @click="open = !open"
    >
      <div
        class="basis-full py-24"
      >
        {{ label }}
      </div>
      <div
        v-if="selectedCount"
        class="desk:hidden whitespace-nowrap flex items-center pr-12"
      >
        <button
          class="btn btn--text mr-12 !leading-22"
          @click.stop="$emit('reset')"
        >
          {{ $t('filter.button.clear') }}
        </button>
        <div class="h-24 w-24 bg-bg-secondary flex items-center justify-center text-text-subtle type-headline-sm">
          {{ selectedCount }}
        </div>
      </div>
      <div class="basis-24 shrink-0 relative mobOnly:-top-1">
        <img
          src="/icons/angle-right.svg"
          class="desk:hidden w-20 h-20"
          alt=""
        >
        <img
          src="/icons/angle-up.svg"
          class="hidden desk:block w-16 h-16"
          :class="{
            'rotate-180': !open,
            'rotate-0': open,
          }"
          alt=""
        >
      </div>
    </div>

    <transition :name="uiStore.isMobile ? 'fromRight' : ''">
      <component
        :is="uiStore.isMobile ? GenericSideMenu : 'div'"
        v-if="open"
        class="limitHeight"
        content-class="mobOnly:pt-18"
        :title="label"
        :show-back="true"
        position="right"
        @back="open = false"
        @close="$emit('close')"
      >
        <div class="desk:max-h-320 overflow-auto scrollableDiv relative">
          <div class="sticky top-0 w-full h-16 -mb-8 bg-gradient-to-b from-lighter to-transparent pointer-events-none" />
          <div
            v-for="option in options"
            :key="option.code"
          >
            <div class="flex justify-between items-start type-sm mb-4 pr-4">
              <div class="basis-24 shrink-0 mr-8">
                <input
                  :id="`mb-${filterKey}-${option.code}`"
                  type="checkbox"
                  :checked="option.selected"
                  @change="toggle(option.code, ($event.target as HTMLInputElement).checked)"
                >
              </div>
              <label
                :for="`mb-${filterKey}-${option.code}`"
                class="basis-full"
              >
                {{ resolveLabel(option.label) }}
              </label>
              <label
                :for="`mb-${filterKey}-${option.code}`"
                class="text-dark type-xs"
              >
                ({{ option.count }})
              </label>
            </div>
          </div>
          <div class="sticky bottom-0 w-full h-16 bg-gradient-to-b from-transparent to-lighter pointer-events-none" />
        </div>
      </component>
    </transition>
  </div>
</template>

<script setup lang="ts">
import { useUiStore } from '~/store/ui';
import GenericSideMenu from '~/components/menu/GenericSideMenu.vue';
import type { MediaBankFilterOption } from '#constants/types/mediaBank';

const { $t } = useNuxtApp();
const uiStore = useUiStore();

const props = defineProps<{
  filterKey: string;
  label: string;
  options: MediaBankFilterOption[];
}>();

const emit = defineEmits<{
  (e: 'setFilter', code: string, selected: boolean): void;
  (e: 'reset'): void;
  (e: 'close'): void;
}>();

const open = ref(!uiStore.isMobile);

const selectedCount = computed(() => props.options.filter((o) => o.selected).length);

const toggle = (code: string, selected: boolean) => {
  emit('setFilter', code, selected);
};

// Labels that look like i18n keys (contain a dot) are translated; raw strings are used as-is
const resolveLabel = (label: string) => {
  if (label.includes('.')) {
    return $t(label);
  }
  return label;
};
</script>

<style scoped lang="postcss">
.limitHeight {
  @screen mobOnly {
    height: calc((var(--vh, 1vh) * 100) - 75px);
  }
}
</style>
